import { useState, useEffect } from 'react';
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, QrCode } from 'lucide-react';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { Button, LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';

interface QRCodeDisplayProps {
  orderId: number;
  orderNumber: string;
  className?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ orderId, orderNumber, className }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const { data: blob, isLoading, isError } = useQuery({
    queryKey: queryKeys.myOrderQr(orderId),
    queryFn: () => ordersApi.getMyOrderQrBlob(orderId),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const handleDownload = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `qr-${orderNumber}.png`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12', className)}>
        <LoadingSpinner size="md" />
        <p className="mt-3 text-sm text-gray-400">Загрузка QR-кода…</p>
      </div>
    );
  }

  if (isError || !objectUrl) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12', className)}>
        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
          <QrCode className="w-8 h-8 text-gray-300" />
        </div>
        <p className="text-sm text-gray-500">Не удалось загрузить QR-код</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 inline-block">
        <img
          src={objectUrl}
          alt={`QR-код для заказа ${orderNumber}`}
          className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
        />
      </div>

      <p className="mt-4 text-sm text-gray-500 text-center">
        Покажите этот код при получении заказа
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className="mt-3"
      >
        <Download className="w-4 h-4" />
        Скачать QR
      </Button>
    </div>
  );
};

export default QRCodeDisplay;
